import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import { browserPerformanceTimeOrigin, getActiveSpan, getRootSpan, spanToJSON, getCurrentScope, htmlTreeAsString, SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT, SEMANTIC_ATTRIBUTE_SENTRY_OP, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN } from '@sentry/core';
import { addPerformanceInstrumentationHandler, addInpInstrumentationHandler, isPerformanceEventTiming } from './instrument.js';
import { getBrowserPerformanceAPI, msToSec, startStandaloneWebVitalSpan } from './utils.js';
var LAST_INTERACTIONS = [];
var INTERACTIONS_SPAN_MAP = new Map();

/**
 * 60 seconds is the maximum for a plausible INP value
 * (source: Me)
 */
var MAX_PLAUSIBLE_INP_DURATION = 60;
/**
 * Start tracking INP webvital events.
 */
function startTrackingINP() {
  var performance = getBrowserPerformanceAPI();
  if (performance && browserPerformanceTimeOrigin()) {
    var inpCallback = _trackINP();
    return function () {
      inpCallback();
    };
  }
  return function () {
    return undefined;
  };
}
var INP_ENTRY_MAP = {
  click: 'click',
  pointerdown: 'click',
  pointerup: 'click',
  mousedown: 'click',
  mouseup: 'click',
  touchstart: 'click',
  touchend: 'click',
  mouseover: 'hover',
  mouseout: 'hover',
  mouseenter: 'hover',
  mouseleave: 'hover',
  pointerover: 'hover',
  pointerout: 'hover',
  pointerenter: 'hover',
  pointerleave: 'hover',
  dragstart: 'drag',
  dragend: 'drag',
  drag: 'drag',
  dragenter: 'drag',
  dragleave: 'drag',
  dragover: 'drag',
  drop: 'drag',
  keydown: 'press',
  keyup: 'press',
  keypress: 'press',
  input: 'press'
};

/** Starts tracking the Interaction to Next Paint on the current page. #
 * exported only for testing
 */
function _trackINP() {
  return addInpInstrumentationHandler(_onInp);
}

/**
 * exported only for testing
 */
var _onInp = function _onInp(_ref) {
  var metric = _ref.metric;
  if (metric.value == undefined) {
    return;
  }
  var duration = msToSec(metric.value);

  // We received occasional reports of hour-long INP values.
  // Therefore, we add a sanity check to avoid creating spans for
  // unrealistically long INP durations.
  if (duration > MAX_PLAUSIBLE_INP_DURATION) {
    return;
  }
  var entry = metric.entries.find(function (entry) {
    return entry.duration === metric.value && INP_ENTRY_MAP[entry.name];
  });
  if (!entry) {
    return;
  }
  var interactionId = entry.interactionId;
  var interactionType = INP_ENTRY_MAP[entry.name];

  /** Build the INP span, create an envelope from the span, and then send the envelope */
  var startTime = msToSec(browserPerformanceTimeOrigin() + entry.startTime);
  var activeSpan = getActiveSpan();
  var rootSpan = activeSpan ? getRootSpan(activeSpan) : undefined;

  // We first try to lookup the span from our INTERACTIONS_SPAN_MAP,
  // where we cache the route per interactionId
  var cachedSpan = interactionId != null ? INTERACTIONS_SPAN_MAP.get(interactionId) : undefined;
  var spanToUse = cachedSpan || rootSpan;

  // Else, we try to use the active span.
  // Finally, we fall back to look at the transactionName on the scope
  var routeName = spanToUse ? spanToJSON(spanToUse).description : getCurrentScope().getScopeData().transactionName;
  var name = htmlTreeAsString(entry.target);
  var attributes = _defineProperty(_defineProperty(_defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN, 'auto.http.browser.inp'), SEMANTIC_ATTRIBUTE_SENTRY_OP, "ui.interaction.".concat(interactionType)), SEMANTIC_ATTRIBUTE_EXCLUSIVE_TIME, entry.duration);
  var span = startStandaloneWebVitalSpan({
    name: name,
    transaction: routeName,
    attributes: attributes,
    startTime: startTime
  });
  if (span) {
    span.addEvent('inp', _defineProperty(_defineProperty({}, SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_UNIT, 'millisecond'), SEMANTIC_ATTRIBUTE_SENTRY_MEASUREMENT_VALUE, metric.value));
    span.end(startTime + duration);
  }
};

/**
 * Register a listener to cache route information for INP interactions.
 */
function registerInpInteractionListener() {
  var handleEntries = function handleEntries(_ref2) {
    var entries = _ref2.entries;
    var activeSpan = getActiveSpan();
    var activeRootSpan = activeSpan && getRootSpan(activeSpan);
    entries.forEach(function (entry) {
      if (!isPerformanceEventTiming(entry) || !activeRootSpan) {
        return;
      }
      var interactionId = entry.interactionId;
      if (interactionId == null) {
        return;
      }

      // If the interaction was already recorded before, nothing more to do
      if (INTERACTIONS_SPAN_MAP.has(interactionId)) {
        return;
      }

      // We keep max. 10 interactions in the list, then remove the oldest one & clean up
      if (LAST_INTERACTIONS.length > 10) {
        var last = LAST_INTERACTIONS.shift();
        INTERACTIONS_SPAN_MAP.delete(last);
      }

      // We add the interaction to the list of recorded interactions
      // and store the span for this interaction
      LAST_INTERACTIONS.push(interactionId);
      INTERACTIONS_SPAN_MAP.set(interactionId, activeRootSpan);
    });
  };
  addPerformanceInstrumentationHandler('event', handleEntries);
  addPerformanceInstrumentationHandler('first-input', handleEntries);
}
export { _onInp, _trackINP, registerInpInteractionListener, startTrackingINP };